import { getDisplayQuote } from 'src/helper/work-order-utils';
import { ISSUES_STATUS, ISSUES_STATUS_LABEL } from 'src/views/VendorPortal/WorkOrder/constants';
import type {
  IssueDetailsSidePanelType,
  QuoteDataResponseType,
  QuoteTaskResponseType,
  QuoteTaskType,
  QuoteType,
  QuoteWorkType,
  TaskOptionType,
  WorkOrderDataType,
  WorkOrderIssueType,
  WorkOrderStateType,
} from './types';

/**
 * ORI-593 (QA): the ONE derivation of the Issue Details panel snapshot — the `data` issue plus
 * its sibling `options`. Two call sites need it (openIssueDetailsSidePanel when the vendor opens
 * the panel, and the getWorkOrderDetails().fulfilled re-sync when a refetch supersedes the
 * open-time snapshot), and they must never drift: `options` is what QuoteComponent pairs with
 * `data.quotes`, so a re-sync that patched `data` alone could leave the cancelled snapshot's
 * option ids submitted against a fresh quote task (the ORI-614 class).
 */
export const buildIssueDetailsPanelSnapshot = (
  issue: WorkOrderIssueType,
): Pick<IssueDetailsSidePanelType, 'data' | 'options'> => ({
  data: issue,
  // Use optional chaining to guard against `quotes` being undefined/null when
  // the cancel-trip API returns work_order_issues without the quotes field.
  options:
    (issue.quotes?.length ?? 0) > 0
      ? // ORI-614: getDisplayQuote (not getActiveQuote) — this branch only runs when the issue
        // HAS quotes, so an active-only resolver would clear the options a rejected-only issue
        // renders today (cost, option count, viewQuoteTaskDetails payload). It must resolve the
        // same quote as QuoteComponent's quoteTask, or an edit would send the rejected quote's
        // option ids into an update against the active quote.
        // PEG-775, carried in from main (where this derivation was still inlined in
        // openIssueDetailsSidePanel). Because it now lives in this shared helper the coercion
        // also covers the getWorkOrderDetails().fulfilled re-sync, which the inlined version
        // never did — and it matches how QuoteComponent coerces the same field on both its
        // display and save paths.
        getDisplayQuote(issue.quotes)?.tasks?.[0]?.options?.map((option: TaskOptionType) => ({
          ...option,
          labor_cost_in_cents: Number(option?.labor_cost_in_cents || 0),
        }))
      : // Explicitly clear options so a previously-opened issue's options are
        // never shown as stale when this issue has no quotes.
        undefined,
});

// Add quote response format to source
export const formatQuoteResponseData = (
  response: QuoteDataResponseType,
  state: WorkOrderStateType,
) => {
  if (!response) {
    return { ...state.source };
  }

  // Create quote
  const quote: QuoteType = {
    quote_id: response.quote_id,
    quote_no: response.quote_no,
    submitted_by: response.submitted_by,
    submitted_at: response.submitted_at,
    valid_until: response.valid_until,
    status: {
      label: response.status.label,
      value: response.status.value,
      color_class: response.status.color_class ?? '',
    },
    state: response.status.value,
    quote_reason: response.reason,
    is_active: true,
    quote_estimation_range: {
      min_in_cents: response.quote_estimation_range.min_in_cents,
      max_in_cents: response.quote_estimation_range.max_in_cents,
    },
    quote_total_in_cents: '0',
    tasks: response.tasks?.map((task) => ({
      quote_task_id: task.quote_task_id,
      quote_task_number: 1,
      title: task.title,
      task_type: task.task_type,
      work_order_issue_id: task.work_order_issue.issue_id,
      work_order_issue: {
        issue_id: task.work_order_issue.issue_id,
        status: {
          label: task.work_order_issue.status.label,
          value: task.work_order_issue.status.value,
        },
        abilities: task.work_order_issue.abilities,
      },
      media:
        task.media?.map((media) => ({
          media_uuid: media.media_uuid,
          media_type: media.media_type,
          mime_type: media.mime_type,
          thumbnail: media.thumbnail,
          original: media.original,
        })) || [],
      options: task.options?.map((option) => ({
        option_id: option.option_id,
        option_type: option.option_type,
        description: option.description,
        labor_cost_in_cents: option.labor_cost_in_cents,
        markup_fee_type: option.markup_fee_type,
        markup_fee_type_value: Number(option.markup_fee_type_value),
        markup_fee_in_cents: Number(option.markup_fee_type_value),
        total_cost_in_cents: 0,
        materials:
          option.materials?.map((material) => ({
            material_id: material.material_id,
            label: material.label,
            markup_fee_type: material.markup_fee_type,
            markup_fee_type_value: Number(material.markup_fee_type_value),
            markup_fee_in_cents: material.markup_fee_in_cents,
            cost_in_cents: material.cost_in_cents,
            total_cost_in_cents: material.total_cost_in_cents,
            quantity: material.quantity,
            quantity_type: material.quantity_type,
            cost_type: material.cost_type,
            unit_price_in_cents: material?.unit_price_in_cents ?? null,
          })) || [],
        media:
          option.media?.map((media) => ({
            media_uuid: media.media_uuid,
            media_type: media.media_type,
            mime_type: media.mime_type,
            thumbnail: media.thumbnail,
            original: media.original,
          })) || [],
      })),
    })),
  };

  // Update work order issues to include the quote
  const updatedWorkOrderIssues = state.source.work_order_issues.map((issue) => {
    if (issue.issue_id === response.tasks?.[0]?.work_order_issue?.issue_id) {
      return {
        ...issue,
        status: {
          label: ISSUES_STATUS_LABEL.QUOTED,
          value: ISSUES_STATUS.QUOTED,
        },
        quotes: [quote],
      };
    }
    return issue;
  });

  return {
    ...state.source,
    quotes: [quote],
    work_order_issues: updatedWorkOrderIssues,
  };
};

// Add quote task response format to source
export const formatQuoteTaskResponseData = (
  currentSource: WorkOrderDataType,
  response: QuoteTaskResponseType,
) => {
  const issueId = response.task.work_order_issue?.issue_id;

  // Get work quotes
  const workOrderQuotes = currentSource.quotes || [];
  const workOrderQuoteIndex = workOrderQuotes.findIndex(
    (quote) => quote.quote_id === response.quote_id,
  );
  const workOrderQuote = workOrderQuotes[workOrderQuoteIndex];

  // Get work order quote tasks
  const workOrderQuoteTasks = workOrderQuote.tasks;
  const workOrderQuoteTaskIndex = workOrderQuoteTasks.findIndex(
    (task) => task.quote_task_id === response.task.quote_task_id,
  );
  const workOrderQuoteTask: QuoteTaskType = {
    quote_task_id: response.task.quote_task_id,
    quote_task_number: 1,
    title: response.task.title,
    task_type: response.task.task_type,
    work_order_issue_id: response.task.work_order_issue?.issue_id,
    work_order_issue: {
      issue_id: response.task.work_order_issue?.issue_id,
      status: {
        label: response.task.work_order_issue?.status.label,
        value: response.task.work_order_issue?.status.value,
      },
      abilities: response.task.work_order_issue?.abilities,
    },
    media:
      response.task.media?.map((media) => ({
        media_uuid: media.media_uuid,
        media_type: media.media_type,
        mime_type: media.mime_type,
        thumbnail: media.thumbnail,
        original: media.original,
      })) || [],
    options:
      response.task.options?.map((option) => {
        const materials =
          option.materials?.map((material) => ({
            material_id: material.material_id,
            label: material.label,
            markup_fee_type: material.markup_fee_type,
            markup_fee_type_value: Number(material.markup_fee_type_value),
            markup_fee_in_cents: material.markup_fee_in_cents,
            cost_in_cents: material.cost_in_cents,
            total_cost_in_cents: material.total_cost_in_cents,
            quantity: material.quantity,
            quantity_type: material.quantity_type,
            cost_type: material.cost_type as 'total' | 'per-unit',
            unit_price_in_cents: material.unit_price_in_cents ?? null,
          })) || [];

        const totalMaterialsCost = materials.reduce(
          (sum, material) => sum + material.total_cost_in_cents,
          0,
        );
        const laborCostWithMarkup =
          option.labor_cost_in_cents + Number(option.markup_fee_type_value || 0);
        const totalOptionCost = laborCostWithMarkup + totalMaterialsCost;

        return {
          option_id: option.option_id,
          option_type: option.option_type as QuoteWorkType,
          description: option.description,
          labor_cost_in_cents: option.labor_cost_in_cents,
          markup_fee_type: option.markup_fee_type,
          markup_fee_type_value: Number(option.markup_fee_type_value),
          markup_fee_in_cents: Number(option.markup_fee_type_value),
          total_cost_in_cents: totalOptionCost,
          materials,
          media:
            option.media?.map((media) => ({
              media_uuid: media.media_uuid,
              media_type: media.media_type,
              mime_type: media.mime_type,
              thumbnail: media.thumbnail,
              original: media.original,
            })) || [],
        };
      }) || [],
  };

  // Update or add task to work order quote
  let updatedWorkOrderQuoteTasks: QuoteTaskType[] = [];
  if (workOrderQuoteTaskIndex >= 0) {
    // Update existing task
    updatedWorkOrderQuoteTasks = [...workOrderQuoteTasks];
    updatedWorkOrderQuoteTasks[workOrderQuoteTaskIndex] = workOrderQuoteTask;
  } else {
    // Add new task
    updatedWorkOrderQuoteTasks = [
      ...workOrderQuoteTasks,
      {
        ...workOrderQuoteTask,
        quote_task_number: workOrderQuoteTasks.length + 1,
      },
    ];
  }

  // Update work order quote
  const updatedWorkOrderQuotes = [...workOrderQuotes];
  updatedWorkOrderQuotes[workOrderQuoteIndex] = {
    ...workOrderQuote,
    quote_estimation_range: response.quote_estimation_range
      ? {
          min_in_cents: response.quote_estimation_range.min_in_cents,
          max_in_cents: response.quote_estimation_range.max_in_cents,
        }
      : null,
    quote_total_in_cents: response.quote_estimation_range
      ? response.quote_estimation_range.max_in_cents?.toString()
      : null,
    status: response.status,
    tasks: updatedWorkOrderQuoteTasks,
  };

  // Update work order issues with the task
  const updatedWorkOrderIssues = currentSource.work_order_issues.map((issue) => {
    if (issue.issue_id === issueId) {
      const existingIssueQuotes = issue.quotes || [];
      const existingIssueQuoteIndex = existingIssueQuotes.findIndex(
        (quote) => quote.quote_id === response.quote_id,
      );

      if (existingIssueQuoteIndex >= 0) {
        // Update existing issue quote with task
        const existingIssueQuote = existingIssueQuotes[existingIssueQuoteIndex];
        const existingIssueTasks = existingIssueQuote.tasks || [];

        // Check if task already exists, update or append
        const existingIssueTaskIndex = existingIssueTasks.findIndex(
          (task) => task.quote_task_id === response.task.quote_task_id,
        );

        let updatedIssueTasks: QuoteTaskType[] = [];
        if (existingIssueTaskIndex >= 0) {
          updatedIssueTasks = [...existingIssueTasks];
          updatedIssueTasks[existingIssueTaskIndex] = {
            ...workOrderQuoteTask,
            quote_task_number: existingIssueTaskIndex + 1,
          };
        } else {
          updatedIssueTasks = [
            ...existingIssueTasks,
            {
              ...workOrderQuoteTask,
              quote_task_number: existingIssueTasks.length + 1,
            },
          ];
        }

        // Update the issue quote
        const updatedIssueQuotes = [...existingIssueQuotes];
        updatedIssueQuotes[existingIssueQuoteIndex] = {
          ...existingIssueQuote,
          tasks: updatedIssueTasks,
        };

        return {
          ...issue,
          status: {
            label: response.task.work_order_issue.status.label,
            value: response.task.work_order_issue.status.value,
          },
          quotes: updatedIssueQuotes,
        };
      }

      // Create new issue quote with the task
      const newIssueQuote = {
        quote_id: response.quote_id,
        quote_number: response.quote_no || 1,
        submitted_by: '',
        submitted_at: '',
        valid_until: null,
        status: {
          label: response.status.label,
          value: response.status.value,
          color_class: response.status.color_class ?? '',
        },
        state: response.status.value,
        quote_reason: null,
        is_active: true,
        quote_estimation_range: response.quote_estimation_range
          ? {
              min_in_cents: response.quote_estimation_range.min_in_cents,
              max_in_cents: response.quote_estimation_range.max_in_cents,
            }
          : null,
        quote_total_in_cents: response.quote_estimation_range
          ? response.quote_estimation_range.max_in_cents?.toString()
          : null,
        tasks: [workOrderQuoteTask],
      };

      return {
        ...issue,
        status: {
          label: response.task.work_order_issue.status.label,
          value: response.task.work_order_issue.status.value,
        },
        quotes: [...existingIssueQuotes, newIssueQuote],
      };
    }
    return issue;
  });

  return {
    ...currentSource,
    quotes: updatedWorkOrderQuotes,
    work_order_issues: updatedWorkOrderIssues,
  };
};
